import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Table, Input, Button, Checkbox, Modal, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { pwh5m8Api, ReverseCandidate } from '../../services/pwh5m8Api';
import { BatchAcceptanceBridge, pwh5m8f1AcceptanceManifest } from '../../features/acceptance';
import './PWH5M8Form.css';

// ---- Item state with selection ----
interface ItemState {
  item: ReverseCandidate;
  selected: boolean;
}

// ---- Component ----
const PWH5M8Form: React.FC = () => {
  const navigate = useNavigate();
  const [startNo, setStartNo] = useState('');
  const [endNo, setEndNo] = useState('');
  const [items, setItems] = useState<ItemState[]>([]);
  const [loading, setLoading] = useState(false);
  const [executing, setExecuting] = useState(false);
  const [queried, setQueried] = useState(false);
  const [lastApiResponse, setLastApiResponse] = useState<unknown>(null);

  // ---- Derived state ----
  const selectedCount = items.filter((s) => s.selected).length;
  const allSelected = items.length > 0 && items.every((s) => s.selected);
  const someSelected = items.some((s) => s.selected) && !allSelected;

  // ---- Query ----
  const onQuery = useCallback(async () => {
    setLoading(true);
    setQueried(false);
    try {
      const res = await pwh5m8Api.queryCandidates(startNo, endNo);
      setLastApiResponse(res);
      const mapped: ItemState[] = (res.items || []).map((item) => ({
        item,
        selected: false,
      }));
      setItems(mapped);
      setQueried(true);
      if (mapped.length === 0) {
        message.info('查無符合條件的請款單');
      } else {
        message.success(`查詢到 ${mapped.length} 筆資料`);
      }
    } catch (e: unknown) {
      const err = e as { response?: { data?: { error?: string } } };
      message.error(err.response?.data?.error || '查詢失敗');
    } finally {
      setLoading(false);
    }
  }, [startNo, endNo]);

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') onQuery();
    },
    [onQuery]
  );

  // ---- Toggle selection ----
  const handleToggle = useCallback((no: string, checked: boolean) => {
    setItems((prev) =>
      prev.map((s) =>
        s.item.fh110_01 === no ? { ...s, selected: checked } : s
      )
    );
  }, []);

  const handleSelectAll = useCallback((checked: boolean) => {
    setItems((prev) => prev.map((s) => ({ ...s, selected: checked })));
  }, []);

  // ---- Execute reversal ----
  const onExecute = useCallback(() => {
    if (selectedCount === 0) {
      message.warning('請先勾選要沖銷的請款單');
      return;
    }
    Modal.confirm({
      title: '確認核准沖銷',
      content: `確定要沖銷已勾選的 ${selectedCount} 筆請款單？此操作不可復原。`,
      okText: '確定沖銷',
      okType: 'primary',
      cancelText: '取消',
      onOk: async () => {
        const selectedNos = items
          .filter((s) => s.selected)
          .map((s) => s.item.fh110_01);
        setExecuting(true);
        try {
          const res = await pwh5m8Api.reverse(selectedNos);
          setLastApiResponse(res);
          if (res.failed === 0) {
            message.success(res.message || `沖銷完成，成功 ${res.success} 筆`);
          } else {
            message.warning(
              res.message || `成功 ${res.success} 筆，失敗 ${res.failed} 筆`
            );
            if (res.errors && res.errors.length > 0) {
              res.errors.forEach((err) => message.error(err));
            }
          }
          setItems([]);
          setQueried(false);
        } catch (e: unknown) {
          const err = e as { response?: { data?: { error?: string } } };
          message.error(err.response?.data?.error || '沖銷執行失敗');
        } finally {
          setExecuting(false);
        }
      },
    });
  }, [selectedCount, items]);

  // ---- Table columns ----
  const columns: ColumnsType<ItemState> = [
    {
      title: (
        <Checkbox
          checked={allSelected}
          indeterminate={someSelected}
          onChange={(e) => handleSelectAll(e.target.checked)}
        />
      ),
      dataIndex: 'selected',
      key: 'selected',
      width: 50,
      align: 'center',
      render: (_: unknown, record: ItemState) => (
        <Checkbox
          checked={record.selected}
          onChange={(e) => handleToggle(record.item.fh110_01, e.target.checked)}
        />
      ),
    },
    {
      title: '請款單號',
      dataIndex: ['item', 'fh110_01'],
      key: 'fh110_01',
      width: 100,
    },
    {
      title: '請款日期',
      dataIndex: ['item', 'fh110_11'],
      key: 'fh110_11',
      width: 110,
    },
    {
      title: '請款金額',
      dataIndex: ['item', 'fh110_12'],
      key: 'fh110_12',
      width: 110,
      align: 'right',
    },
    {
      title: '含稅總額',
      dataIndex: ['item', 'fh110_13'],
      key: 'fh110_13',
      width: 110,
      align: 'right',
    },
    {
      title: '公司代碼',
      dataIndex: ['item', 'fh110_16'],
      key: 'fh110_16',
      width: 100,
    },
    {
      title: '請款類別',
      dataIndex: ['item', 'fh110_221'],
      key: 'fh110_221',
      width: 100,
    },
    {
      title: '部門代碼',
      dataIndex: ['item', 'fh110_37'],
      key: 'fh110_37',
      width: 90,
    },
    {
      title: '核准層級',
      dataIndex: ['item', 'fh110_802'],
      key: 'fh110_802',
      width: 100,
    },
  ];

  // ---- Render ----
  return (
    <div className="pwh5m8-form">
      {(loading || executing) && (
        <div className="loading-overlay">
          {executing ? '沖銷執行中...' : '載入中...'}
        </div>
      )}

      <div className="page-wrapper">
        {/* Page Title Bar */}
        <div className="page-title-bar">
          <div className="page-title-left">
            <nav className="breadcrumb-inline">
              <a
                href="#/erp"
                onClick={(e) => {
                  e.preventDefault();
                  navigate('/erp');
                }}
              >
                首頁
              </a>
              <span>/</span>
              <a
                href="#/erp"
                onClick={(e) => {
                  e.preventDefault();
                  navigate('/erp');
                }}
              >
                請款管理
              </a>
              <span>/</span>
              <span className="current">請款單核准沖銷</span>
            </nav>
            <span className="title-separator">&mdash;</span>
            <h1>請款單核准沖銷</h1>
            <span className="badge">PWH5M8</span>
          </div>
        </div>

        {/* Query Bar */}
        <div className="query-bar">
          <label data-contract-field="fh110_01">請款單號 .從:</label>
          <Input
            value={startNo}
            onChange={(e) => setStartNo(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="起始單號"
            style={{ width: 160 }}
            allowClear
          />
          <span className="separator">至</span>
          <Input
            value={endNo}
            onChange={(e) => setEndNo(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="結束單號"
            style={{ width: 160 }}
            allowClear
          />
          <Button type="primary" onClick={onQuery} loading={loading} data-acceptance-action="PWH5M8-ACT-QUERY">
            確定範圍
          </Button>
        </div>

        {/* Table Card */}
        <div className="table-card">
          {queried && (
            <div className="table-header">
              <div className="info">
                共 <strong>{items.length}</strong> 筆
                {selectedCount > 0 && (
                  <span>
                    ，已勾選 <strong>{selectedCount}</strong> 筆
                  </span>
                )}
              </div>
            </div>
          )}

          {queried ? (
            <Table<ItemState>
              columns={columns}
              dataSource={items}
              rowKey={(record) => record.item.fh110_01}
              pagination={false}
              size="small"
              scroll={{ y: 420 }}
              locale={{ emptyText: '查無資料' }}
            />
          ) : (
            <div className="empty-state">
              請輸入請款單號範圍並點擊「確定範圍」查詢
            </div>
          )}
        </div>

        {/* Bottom Action Bar */}
        <div className="action-bar">
          <Button
            type="primary"
            size="large"
            disabled={selectedCount === 0 || executing}
            loading={executing}
            onClick={onExecute}
            data-acceptance-action="PWH5M8-ACT-REVERSE"
          >
            存檔 ({selectedCount})
          </Button>
          <Button
            danger
            size="large"
            onClick={() => navigate('/erp')}
          >
            離開
          </Button>
        </div>
        <BatchAcceptanceBridge
          manifest={pwh5m8f1AcceptanceManifest}
          records={items.map(state => state.item)}
          selectedKeys={items.filter(state => state.selected).map(state => state.item.fh110_01)}
          keyOf={(item) => item.fh110_01}
          queryExact={async (key) => (await pwh5m8Api.queryCandidates(key, key)).items?.[0] || null}
          lastApiResponse={lastApiResponse}
          loading={loading || executing}
          onAutoFill={() => setItems(prev => prev.map((state, index) => ({ ...state, selected: index === 0 })))}
          autoFillDisabled={items.length === 0}
          autoFillReason="只選取正式 API 回傳的第一筆應付請款沖銷候選。"
          screenDraft={{ start_no: startNo, end_no: endNo, selected_items: items.filter(state => state.selected) }}
        />
      </div>
    </div>
  );
};

export default PWH5M8Form;
